/**
 * Navigation Color Picker
 * 
 * Color picker for navigation items (Home, Entertainment screens)
 * Shows color swatches without labels - simple visual selection
 */

import React from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

// Navigation category colors (original 4 semantic colors for defaults)
export const navigationColors = [
  { 
    id: "teal", 
    bg: "bg-teal-600/20", 
    border: "border-teal-600", 
    iconColor: "text-teal-600",
    hex: "#0d9488"
  },
  { 
    id: "green", 
    bg: "bg-green-500/20", 
    border: "border-green-500", 
    iconColor: "text-green-500",
    hex: "#22c55e"
  },
  { 
    id: "blue", 
    bg: "bg-blue-500/20", 
    border: "border-blue-500", 
    iconColor: "text-blue-400",
    hex: "#3b82f6"
  },
  { 
    id: "orange", 
    bg: "bg-orange-500/20", 
    border: "border-orange-500", 
    iconColor: "text-orange-400",
    hex: "#f97316"
  },
  { 
    id: "pink", 
    bg: "bg-pink-500/20", 
    border: "border-pink-500", 
    iconColor: "text-pink-400",
    hex: "#ec4899"
  },
  { 
    id: "help", 
    bg: "bg-destructive/20", 
    border: "border-destructive", 
    iconColor: "text-destructive",
    hex: "#ef4444"
  },
  { 
    id: "purple", 
    bg: "bg-purple-500/20", 
    border: "border-purple-500", 
    iconColor: "text-purple-400",
    hex: "#a855f7"
  },
  { 
    id: "yellow", 
    bg: "bg-yellow-500/20", 
    border: "border-yellow-500", 
    iconColor: "text-yellow-400",
    hex: "#eab308"
  },
  { 
    id: "indigo", 
    bg: "bg-indigo-500/20", 
    border: "border-indigo-500", 
    iconColor: "text-indigo-400",
    hex: "#6366f1"
  },
];

export type NavigationColorId = typeof navigationColors[number]["id"];

export function getNavigationColorClasses(colorId: string) {
  return navigationColors.find(c => c.id === colorId) || navigationColors[2]; // default to utility
}

interface NavigationColorPickerProps {
  selectedColor?: string;
  onSelect: (colorId: string) => void;
}

export function NavigationColorPicker({ selectedColor, onSelect }: NavigationColorPickerProps) {
  return (
    <div className="flex flex-wrap gap-3">
      {navigationColors.map((color) => (
        <button
          key={color.id}
          onClick={() => onSelect(color.id)}
          className={cn(
            "w-12 h-12 rounded-xl border-2 transition-all flex items-center justify-center",
            "hover:scale-110",
            color.bg,
            color.border,
            selectedColor === color.id && "ring-2 ring-offset-2 ring-primary ring-offset-background"
          )}
        >
          {selectedColor === color.id && (
            <Check className={cn("w-5 h-5", color.iconColor)} />
          )}
        </button>
      ))}
    </div>
  );
}
